import { Clock, Phone, Copy, Check, Shield, Loader2, Mic, Volume2 } from "lucide-react";
import { useState, useCallback } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/components/ui/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

interface InsuraABotKeyCode {
  id: string;
  code: string;
  timestamp: Date;
  duration: number;
  callId: string;
}

interface RecentCollection {
  digits: string;
  type: string;
  formatted: string;
  timestamp: Date;
}

interface InsuraABotKeyGrabberProps {
  codes: InsuraABotKeyCode[];
  isDTMFListening: boolean;
  setIsDTMFListening: (listening: boolean) => void; // NEW: Control listening state from button press
  callId?: string | null; // Add callId prop for triggering sensitive data collection
  isSpeechProcessing?: boolean; // NEW: Track if speech is being converted to digits
  onClearCodes?: () => void; // NEW: Callback to clear codes when starting a new collection
}

/**
 * Auto-detect and format SSN only
 * For other data, keeps letters and numbers as-is
 */
function detectAndFormatPattern(digits: string): { formatted: string; type: string } {
  // Keep letters and numbers
  const cleanedWithLetters = digits.replace(/[^A-Za-z0-9]/g, '');
  // For SSN: Only numbers
  const cleanedDigitsOnly = digits.replace(/\D/g, '');

  // SSN: 9 digits only → XXX-XX-XXXX
  if (cleanedDigitsOnly.length === 9 && cleanedWithLetters.length === 9) {
    return {
      formatted: `${cleanedDigitsOnly.slice(0, 3)}-${cleanedDigitsOnly.slice(3, 5)}-${cleanedDigitsOnly.slice(5, 9)}`,
      type: 'SSN'
    };
  }

  // Default: return alphanumeric data as-is (for policy numbers, account numbers, etc.)
  return {
    formatted: cleanedWithLetters.toUpperCase() || digits,
    type: 'Number'
  };
}

export const InsuraABotKeyGrabber = function({ codes, isDTMFListening, setIsDTMFListening, callId, isSpeechProcessing = false, onClearCodes }: InsuraABotKeyGrabberProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isCollecting, setIsCollecting] = useState(false);
  const [recentCollections, setRecentCollections] = useState<RecentCollection[]>([]);
  const [currentCollectionType, setCurrentCollectionType] = useState<'ssn' | 'other' | null>(null);
  const [activeCollectionType, setActiveCollectionType] = useState<'ssn' | 'other' | null>(null);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [collectionStartTime, setCollectionStartTime] = useState<Date | null>(null); // Track when collection started
  const { toast } = useToast();

  // Filter codes to only show those from the CURRENT collection
  // This prevents old SSN data from showing when a new collection starts
  const filteredCodes = collectionStartTime
    ? codes.filter(c => c.timestamp >= collectionStartTime)
    : codes;

  // Get all digits as a single string (only from current collection)
  const allDigits = filteredCodes.map(c => c.code).join('');

  // Determine type based on what button was pressed, not auto-detection
  let formatted: string;
  let type: string;

  if (currentCollectionType === 'ssn') {
    // Always treat as SSN if SSN button was pressed
    const cleanedDigitsOnly = allDigits.replace(/\D/g, '');
    if (cleanedDigitsOnly.length === 9) {
      formatted = `${cleanedDigitsOnly.slice(0, 3)}-${cleanedDigitsOnly.slice(3, 5)}-${cleanedDigitsOnly.slice(5, 9)}`;
    } else {
      formatted = cleanedDigitsOnly;
    }
    type = 'SSN';
  } else if (currentCollectionType === 'other') {
    // Treat as Other Private Info if Other button was pressed (can be long)
    const cleanedWithLetters = allDigits.replace(/[^A-Za-z0-9]/g, '');
    formatted = cleanedWithLetters.toUpperCase();
    type = 'Other Info';
  } else {
    // Fallback to auto-detection if no button was pressed
    const result = detectAndFormatPattern(allDigits);
    formatted = result.formatted;
    type = result.type;
  }

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCollectSensitiveInfo = useCallback(async (dataType: 'ssn' | 'other') => {
    if (!callId) {
      toast({
        title: "No Active Call",
        description: "Cannot collect sensitive information without an active call",
        variant: "destructive",
      });
      return;
    }

    // Prevent concurrent calls - if already collecting, ignore
    if (isCollecting || activeCollectionType !== null) {
      return;
    }

    // Debounce: Prevent true double-clicks only (300ms)
    const now = Date.now();
    if (now - lastClickTime < 300) {
      return;
    }
    setLastClickTime(now);

    // Save current collection to recent if we have codes from the CURRENT collection
    // Use filteredCodes (filtered by collectionStartTime) to only capture this session's data
    const currentFilteredCodes = collectionStartTime
      ? codes.filter(c => c.timestamp >= collectionStartTime)
      : codes;

    if (currentFilteredCodes.length > 0 && currentCollectionType) {
      const currentDigits = currentFilteredCodes.map(c => c.code).join('');

      // Compute formatted value fresh from the filtered codes (not the stale closure value)
      let savedFormatted: string;
      if (currentCollectionType === 'ssn') {
        const cleanedDigitsOnly = currentDigits.replace(/\D/g, '');
        if (cleanedDigitsOnly.length === 9) {
          savedFormatted = `${cleanedDigitsOnly.slice(0, 3)}-${cleanedDigitsOnly.slice(3, 5)}-${cleanedDigitsOnly.slice(5, 9)}`;
        } else {
          savedFormatted = cleanedDigitsOnly;
        }
      } else if (currentCollectionType === 'other') {
        const cleanedWithLetters = currentDigits.replace(/[^A-Za-z0-9]/g, '');
        savedFormatted = cleanedWithLetters.toUpperCase();
      } else {
        const result = detectAndFormatPattern(currentDigits);
        savedFormatted = result.formatted;
      }

      // Only save if we actually have meaningful data
      if (savedFormatted && savedFormatted.length > 0) {
        // Determine the type label based on CURRENT collection type (not auto-detection)
        let collectionTypeLabel: string;
        if (currentCollectionType === 'ssn') {
          collectionTypeLabel = 'SSN';
        } else if (currentCollectionType === 'other') {
          collectionTypeLabel = 'Other Info';
        } else {
          collectionTypeLabel = type;
        }

        setRecentCollections(prev => [...prev, {
          digits: currentDigits,
          type: collectionTypeLabel,
          formatted: savedFormatted,
          timestamp: new Date()
        }]);
      }
    }

    // IMPORTANT: Set the new collection type and start time FIRST
    // This ensures old codes are filtered out immediately
    setCurrentCollectionType(dataType);
    setActiveCollectionType(dataType);
    setCollectionStartTime(new Date()); // Track when this collection started
    setIsCollecting(true);

    // Clear the current codes immediately when starting a new collection
    // This ensures the blue box clears right away and shows new digits
    if (onClearCodes) {
      onClearCodes();
    }

    // IMPORTANT: Enable "Is Listening" state when button is pressed
    // This shows the green badge and indicates collection is active
    setIsDTMFListening(true);

    try {
      const response = await api.post({
        url: getApiUrl(`/api/communication/calls/${callId}/collect-sensitive`),
        auth: true,
        body: { dataType }, // Pass dataType to backend (changed from 'data' to 'body')
      });

      const typeLabel = dataType === 'ssn' ? 'Social Security Number' : 'Other Private Information';
      const description = dataType === 'ssn'
        ? "Lead can enter 9 digits using keypad or speak them clearly"
        : "Lead can enter or say any information (numbers, letters). Works for long data like policy numbers, account numbers, etc.";

      toast({
        title: `Collecting ${typeLabel}`,
        description: description,
        duration: 6000,
      });
    } catch (error: any) {
      toast({
        title: "Failed to Collect Information",
        description: error?.message || "Could not initiate sensitive data collection",
        variant: "destructive",
      });
    } finally {
      setIsCollecting(false);
      setActiveCollectionType(null);
    }
  }, [callId, codes, currentCollectionType, collectionStartTime, type, isCollecting, activeCollectionType, lastClickTime, setIsDTMFListening, onClearCodes, toast]);

  return (
    <Card className="w-full" data-tour="insura-bot">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Insura-A-Bot Key Grabber
            </CardTitle>
            <Badge variant="outline" className="text-[10px] px-3 py-0.5 w-fit bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-300 font-semibold tracking-wide">
              Patent Pending
            </Badge>
            <Badge
              variant={isDTMFListening ? "default" : "secondary"}
              className={isDTMFListening ? "bg-green-600 w-fit" : "w-fit"}
            >
              {isDTMFListening && (
                <div className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse" />
              )}
              {isDTMFListening ? "Listening" : "Not Listening"}
            </Badge>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCollectSensitiveInfo('ssn');
              }}
              disabled={!callId || isCollecting || activeCollectionType !== null}
              className="text-xs h-7 w-[110px]"
            >
              {(isCollecting && activeCollectionType === 'ssn') ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Collecting...
                </>
              ) : (
                <>
                  <Shield className="h-3 w-3 mr-1" />
                  Collect SSN
                </>
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCollectSensitiveInfo('other');
              }}
              disabled={!callId || isCollecting || activeCollectionType !== null}
              className="text-xs h-7 w-[110px]"
            >
              {(isCollecting && activeCollectionType === 'other') ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Collecting...
                </>
              ) : (
                <>
                  <Shield className="h-3 w-3 mr-1" />
                  Other Info
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Speech Processing Indicator - Shows when voice input is being converted */}
        {isDTMFListening && isSpeechProcessing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 animate-pulse">
            <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Processing speech...</p>
              <p className="text-xs text-amber-600">Converting spoken digits to numbers</p>
            </div>
          </div>
        )}

        {/* Listening Indicator - Shows when DTMF is active and NO speech processing */}
        {isDTMFListening && !isSpeechProcessing && filteredCodes.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <Mic className="h-4 w-4 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Listening for input...</p>
              <p className="text-xs text-blue-600">Customer can use keypad or speak letters and numbers</p>
            </div>
          </div>
        )}

        {filteredCodes.length === 0 && !isDTMFListening ? (
          <div className="text-sm text-muted-foreground space-y-2 py-3">
            <p className="text-center py-1">No key codes detected</p>
            <div className="border-t pt-2">
              <p className="text-xs leading-relaxed">
                Click 'Collect SSN' or 'Other Info' to securely collect sensitive information. They can enter digits using their phone keypad or speak numbers/letters clearly. Codes will appear here automatically.
              </p>
            </div>
          </div>
        ) : filteredCodes.length > 0 ? (
          <>
            {/* Formatted Display */}
            {allDigits.length >= 6 && (
              <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs font-medium text-blue-700 mb-1">{type}</div>
                    <div className="text-2xl font-mono font-bold text-blue-900 tracking-wide">
                      {formatted}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      {allDigits.length} digit{allDigits.length !== 1 ? 's' : ''} entered
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-2"
                    onClick={() => handleCopy(formatted, 'formatted')}
                  >
                    {copiedId === 'formatted' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Individual Digits */}
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Individual Digits</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {filteredCodes.slice(-10).map((code) => (
                  <div
                    key={code.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Badge className="font-mono" variant="outline">
                        {code.code}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {code.duration}s
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {code.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null
        }

        {/* Recent Collections Section */}
        {recentCollections.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">Recent Collections</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {recentCollections.slice(-3).reverse().map((collection, index) => (
                <div
                  key={`${collection.timestamp.getTime()}-${index}`}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="text-xs" variant="outline">
                        {collection.type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {collection.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-sm font-mono font-semibold text-gray-800">
                      {collection.formatted}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-2"
                    onClick={() => handleCopy(collection.formatted, `recent-${index}`)}
                  >
                    {copiedId === `recent-${index}` ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
